import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import apiClient from '../../utils/api';

const LIMIT = 20;
const ROLES = ['', 'SUPER_ADMIN', 'COMPANY_ADMIN', 'DISPATCH_AGENT', 'RECOVERY_OFFICER', 'COMPANY_AGENT', 'INDEPENDENT_USER', 'TRANSPORTER', 'WAREHOUSE_MANAGER', 'INVENTORY_AUDITOR'];

export const SuperAdminUsers: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.listCompanies(200, 0).then(r => {
      const d = r.data.data;
      setCompanies(d?.companies ?? d?.rows ?? (Array.isArray(d) ? d : []));
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.listUsers(companyFilter || undefined, LIMIT, page * LIMIT, search || undefined, roleFilter || undefined);
      const d = res.data.data;
      setUsers(d?.users ?? d?.rows ?? (Array.isArray(d) ? d : []));
      setTotal(d?.total ?? d?.count ?? 0);
    } catch {} finally { setLoading(false); }
  }, [page, search, roleFilter, companyFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (u: any) => {
    const next = u.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    await apiClient.updateUser(u.id, { status: next });
    load();
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">All Users <span style={{ fontSize: 14, color: '#94a3b8', fontWeight: 400 }}>({total.toLocaleString()})</span></h1>
        <Link to="/super-admin/users/new" className="btn btn-primary"><Plus size={16} /> New User</Link>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input placeholder="Search by name or email..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              style={{ paddingLeft: 32, width: '100%' }} />
          </div>
          <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(0); }} style={{ width: 190 }}>
            {ROLES.map(r => <option key={r} value={r}>{r || 'All Roles'}</option>)}
          </select>
          <select value={companyFilter} onChange={e => { setCompanyFilter(e.target.value); setPage(0); }} style={{ width: 190 }}>
            <option value="">All Companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button className="btn btn-ghost" onClick={() => { setSearch(''); setRoleFilter(''); setCompanyFilter(''); setPage(0); }}>Clear</button>
        </div>

        {loading ? <div className="loading-spinner">Loading users...</div> : users.length === 0 ? (
          <div className="empty-state"><h3>No users found</h3></div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Role</th><th>Company</th><th>Branch</th><th>Status</th><th>Last Login</th><th></th></tr>
                </thead>
                <tbody>
                  {users.map((u: any) => (
                    <tr key={u.id}>
                      <td style={{ fontWeight: 500 }}>{u.firstName} {u.lastName}</td>
                      <td>{u.email}</td>
                      <td><span className="badge badge-inactive" style={{ fontSize: 10 }}>{u.role.replace(/_/g, ' ')}</span></td>
                      <td>{u.company?.name ?? u.companyId ?? '—'}</td>
                      <td>{u.branch?.name ?? u.branchId ?? '—'}</td>
                      <td><span className={`badge badge-${u.status?.toLowerCase()}`}>{u.status}</span></td>
                      <td style={{ fontSize: 12, color: '#94a3b8' }}>{u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : '—'}</td>
                      <td style={{ display: 'flex', gap: 6 }}>
                        <Link to={`/super-admin/users/${u.id}/edit`} className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>Edit</Link>
                        <button className={`btn ${u.status === 'ACTIVE' ? 'btn-danger' : 'btn-success'}`} style={{ padding: '3px 8px', fontSize: 12 }} onClick={() => toggleStatus(u)}>
                          {u.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
